/**
 * Who hears about a failing backup, and when.
 *
 * RECIPIENTS ARE STRICT OPT-IN and the panel says so out loud. There is no
 * implicit fallback to owners or admins: defaulting to "whoever looks like an
 * administrator" would mean the first collector run after this shipped emailed
 * clients who never asked to hear from it, and that is not a mistake you can
 * take back. The cost is that an org which never opens this page is never
 * alerted, so the empty state states that plainly rather than looking tidy.
 */

import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

interface Settings {
  enabled: boolean;
  recipients: string[];
  immediate: boolean;
  recovery: boolean;
  digest: boolean;
}

const EMPTY: Settings = {
  enabled: false, recipients: [], immediate: true, recovery: true, digest: true,
};

export function AlertSettings({ orgId, canManage }: { orgId: string | null; canManage: boolean }) {
  const [s, setS] = useState<Settings | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    if (!orgId) return;
    void (async () => {
      try {
        const r = await httpsCallable(functions, 'getAlertSettings')({ orgId });
        const v = r.data as Settings;
        setS(v);
        setText(v.recipients.join('\n'));
      } catch {
        setS({ ...EMPTY });
      }
    })();
  }, [orgId]);

  if (!orgId || !s) return null;

  // One per line rather than comma-separated: addresses get pasted out of a
  // mail client a line at a time, and a stray comma inside a display name is
  // the kind of thing that silently truncates a list.
  const recipients = text.split(/[\n,;]+/).map((x) => x.trim()).filter(Boolean);

  async function run(key: string, fn: () => Promise<void>) {
    setBusy(key); setMsg(null);
    try { await fn(); }
    catch (err) { setMsg({ ok: false, text: err instanceof Error ? err.message : 'That did not work.' }); }
    finally { setBusy(null); }
  }

  const save = () => run('save', async () => {
    const r = await httpsCallable(functions, 'setAlertSettings')({ orgId, ...s, recipients });
    const v = r.data as Settings;
    setS(v);
    setText(v.recipients.join('\n'));
    setMsg({ ok: true, text: 'Saved.' });
  });

  const test = () => run('test', async () => {
    const r = await httpsCallable(functions, 'sendTestAlert')({ orgId });
    const n = (r.data as { sentTo: number }).sentTo;
    setMsg({ ok: true, text: `Sent to ${n} recipient${n === 1 ? '' : 's'}.` });
  });

  const live = s.enabled && s.recipients.length > 0;

  return (
    <section className="panel">
      <h2 className="panel-title">
        Alerts
        <span className={'tag ' + (live ? 'tag-ok' : 'tag-warn')} style={{ marginLeft: 10 }}>
          {live ? 'on' : 'off'}
        </span>
      </h2>
      <p className="panel-note">
        Email when a backup starts failing. Alerts fire on the change, not on every sync - a machine
        that stays broken over a weekend produces one email, not ninety-six.
      </p>

      {!s.recipients.length && (
        <div className="banner banner-warn">
          No recipients, so nothing is being sent. Alerts are never sent to an address that has not
          been listed here.
        </div>
      )}
      {msg && <div className={'banner ' + (msg.ok ? 'banner-ok' : 'banner-error')}>{msg.text}</div>}

      <label className="check-line">
        <input type="checkbox" checked={s.enabled} disabled={!canManage}
               onChange={(e) => setS({ ...s, enabled: e.target.checked })} />
        <span>Send alerts for this organisation</span>
      </label>

      <label className="field" style={{ marginTop: 12 }}>
        <span className="field-label">Recipients - one address per line</span>
        <textarea
          className="field-input" rows={4} disabled={!canManage}
          placeholder={'someone@example.com\nsomeone.else@example.com'}
          value={text} onChange={(e) => setText(e.target.value)}
        />
      </label>

      <label className="check-line">
        <input type="checkbox" checked={s.immediate} disabled={!canManage}
               onChange={(e) => setS({ ...s, immediate: e.target.checked })} />
        <span>Immediately, when a machine starts failing</span>
      </label>
      <label className="check-line">
        <input type="checkbox" checked={s.recovery} disabled={!canManage}
               onChange={(e) => setS({ ...s, recovery: e.target.checked })} />
        <span>When a failing machine recovers</span>
      </label>
      <label className="check-line">
        <input type="checkbox" checked={s.digest} disabled={!canManage}
               onChange={(e) => setS({ ...s, digest: e.target.checked })} />
        <span>A daily summary at 8am, on days when something needs attention</span>
      </label>

      {canManage && (
        <div className="row-form" style={{ marginTop: '.7rem' }}>
          <button className="btn btn-primary" disabled={busy !== null} onClick={save}>
            {busy === 'save' ? 'Saving...' : 'Save'}
          </button>
          <button
            className="btn" disabled={busy !== null || !s.recipients.length}
            title={s.recipients.length ? 'Send a real email to the saved recipients' : 'Save a recipient first'}
            onClick={test}
          >
            {busy === 'test' ? 'Sending...' : 'Send test'}
          </button>
        </div>
      )}

      <p className="panel-note">
        Test sends to the SAVED list, so save first. It proves the whole path - the Resend
        credential, the sending domain and the addresses - rather than only that they were typed in.
      </p>
    </section>
  );
}
